import {Instant, Now, PlainDate, PlainDateTime, ZonedDateTime} from "@variocube/app-ui";
import {SiteAccessibility} from "./data/client.gen";

export type Weekday = keyof SiteAccessibility;
export const WEEKDAYS: Weekday[] = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export function getWeekday(daysInWeek: number) {
	return WEEKDAYS[daysInWeek - 1];
}

export function getWeekdayFromDate(datetime: ZonedDateTime) {
	return getWeekday(datetime.dayOfWeek);
}

export function getStartOfDay(datetime: ZonedDateTime) {
	return datetime.with({
		hour: 0,
		minute: 0,
		second: 0,
		millisecond: 0,
	});
}

export function parseTime(time: string | null | undefined) {
	if (time) {
		const parts = time.split(":");
		try {
			return {
				hour: Number.parseInt(parts[0]),
				minute: Number.parseInt(parts[1]) || 0,
				second: Number.parseInt(parts[2]) || 0,
			};
		}
		catch (error) {
			console.error("Could not parse time", time);
			return undefined;
		}
	}
}

export function isValidPeriod(
	from: ZonedDateTime | undefined,
	until: ZonedDateTime | undefined,
	minDate: ZonedDateTime | undefined,
	maxDate: ZonedDateTime | undefined,
) {
	if (from === undefined || until === undefined) {
		console.error("Either from or until date is not defined.");
		return false;
	}
	if (PlainDate.compare(from.toPlainDate(), until.toPlainDate()) > 0) {
		console.error("From date is after until date.");
		return false;
	}
	if (minDate !== undefined && PlainDate.compare(minDate.toPlainDate(), from.toPlainDate()) > 0) {
		console.error("From date is before min. date.");
		return false;
	}
	if (maxDate !== undefined && PlainDate.compare(maxDate.toPlainDate(), until.toPlainDate()) < 0) {
		console.error(`Until date is after max. date. maxDate (${maxDate.toJSON()}), until: (${until.toJSON()})`);
		return false;
	}
	return true;
}

function secondsOfDay(time: { hour: number; minute: number; second: number }) {
	return time.hour * 3600 + time.minute * 60 + time.second;
}

/**
 * Returns the closing instant of the opening window that contains `from`, or `undefined` if `from` is outside hours.
 * Mirrors the backend owner-day rule: a window owned by the previous day can extend past midnight (e.g. 22:00–06:00),
 * so both `from`'s day and the previous day are checked. A day with no configured `from`/`until` is treated as closed.
 * Half-open `[open, until)` — the closing instant is not itself within hours. Callers must short-circuit
 * always-accessible / fully-unconfigured sites (24/7).
 */
export function getOpeningHoursEnd(from: ZonedDateTime, accessibility: SiteAccessibility) {
	for (const anchor of [from, from.subtract({days: 1})]) {
		const acc = accessibility[getWeekdayFromDate(anchor)];
		const open = parseTime(acc?.from);
		const until = parseTime(acc?.until);
		if (!open || !until) {
			continue;
		}
		const start = getStartOfDay(anchor).with(open);
		// `until <= open` means the window wraps past midnight into the next day.
		const endAnchor = secondsOfDay(until) <= secondsOfDay(open) ? anchor.add({days: 1}) : anchor;
		const end = getStartOfDay(endAnchor).with(until);
		if (ZonedDateTime.compare(from, start) >= 0 && ZonedDateTime.compare(from, end) < 0) {
			return end;
		}
	}
	return undefined;
}

/**
 * True if `from` falls within one of the site's opening windows. See {@link getOpeningHoursEnd}.
 */
export function isWithinOpeningHours(from: ZonedDateTime, accessibility: SiteAccessibility) {
	return getOpeningHoursEnd(from, accessibility) !== undefined;
}

/**
 * Snaps `dt` down to a whole hour (minutes/seconds/sub-second zeroed) so a start carrying stray clock minutes
 * (e.g. 16:11) doesn't eat into the hours bookable before closing. If flooring would land before `min` (the earliest
 * bookable moment), the original `dt` is kept unchanged — so a deliberate sub-hour start near "now" stays as picked.
 */
export function snapToHour(dt: ZonedDateTime, min?: ZonedDateTime) {
	const floored = dt.with({minute: 0, second: 0, millisecond: 0, microsecond: 0, nanosecond: 0});
	if (min !== undefined && ZonedDateTime.compare(floored, min) < 0) {
		return dt;
	}
	return floored;
}

export function parseZoneDateTimeFromInstant(instant: string, zoneId: string) {
	return Instant.from(instant).toZonedDateTimeISO(zoneId);
}

export function parseZoneDateTimeFromDateTime(datetime: string, zoneId: string) {
	return PlainDateTime.from(datetime).toZonedDateTime(zoneId);
}

export function parseZoneDateTimeFromDate(date: string, zoneId: string) {
	return PlainDate.from(date).toZonedDateTime(zoneId);
}

export function getZoneDateTimeNow(zoneId: string) {
	return Now.zonedDateTimeISO(zoneId);
}
